/**
 * Professional Translation Service
 * Handles all translation logic with proper error handling and performance optimization
 */

class TranslationService {
  constructor() {
    this.translations = new Map();
    this.currentLanguage = 'fr';
    this.fallbackLanguage = 'fr';
    this.loadedLanguages = new Set();
    this.cache = new Map();
  }

  /**
   * Initialize the translation service with languages
   * @param {Object} config - Configuration object
   */
  async initialize(config = {}) {
    try {
      this.currentLanguage = config.defaultLanguage || 'fr';
      this.fallbackLanguage = config.fallbackLanguage || 'fr';
      
      // Load translation files
      await this.loadTranslations();
      
      console.log('Translation service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize translation service:', error);
      throw new Error('Translation service initialization failed');
    }
  }

  /**
   * Load translation files dynamically
   */
  async loadTranslations() {
    try {
      const languages = ['fr', 'en'];
      
      for (const lang of languages) {
        if (!this.loadedLanguages.has(lang)) {
          const translations = await this.loadLanguageFile(lang);
          this.translations.set(lang, translations);
          this.loadedLanguages.add(lang);
        }
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
      throw error;
    }
  }

  /**
   * Load a specific language file
   * @param {string} language - Language code
   */
  async loadLanguageFile(language) {
    try {
      const module = await import(`../translations/${language}.json`);
      return module.default;
    } catch (error) {
      console.error(`Failed to load language file for ${language}:`, error);
      
      // Return empty object as fallback
      return {};
    }
  }

  /**
   * Get translation for a key
   * @param {string} key - Translation key (e.g., 'navbar.home')
   * @param {Object} options - Translation options
   */
  translate(key, options = {}) {
    const {
      defaultValue = key,
      interpolation = {},
      language = this.currentLanguage,
      returnObjects = false
    } = options;

    // Create cache key
    const cacheKey = `${language}:${key}:${JSON.stringify(interpolation)}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Get translation value
      let value = this.getNestedValue(key, language);
      
      // If not found, try fallback language
      if (value === undefined && language !== this.fallbackLanguage) {
        value = this.getNestedValue(key, this.fallbackLanguage);
      }
      
      // If still not found, return default value
      if (value === undefined) {
        value = defaultValue;
      }

      // Handle array/object returns
      if (returnObjects && typeof value === 'object') {
        this.cache.set(cacheKey, value);
        return value;
      }

      // Handle string interpolation
      if (typeof value === 'string' && Object.keys(interpolation).length > 0) {
        value = this.interpolateString(value, interpolation);
      }

      // Cache the result
      this.cache.set(cacheKey, value);
      
      return value;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Get nested value from translation object
   * @param {string} key - Dot-separated key
   * @param {string} language - Language code
   */
  getNestedValue(key, language) {
    const translations = this.translations.get(language);
    if (!translations) return undefined;

    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    return value;
  }

  /**
   * Interpolate variables in string
   * @param {string} str - String with placeholders
   * @param {Object} variables - Variables to interpolate
   */
  interpolateString(str, variables) {
    return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key] !== undefined ? variables[key] : match;
    });
  }

  /**
   * Change current language
   * @param {string} language - New language code
   */
  async changeLanguage(language) {
    if (!this.isLanguageSupported(language)) {
      console.warn(`Language "${language}" is not supported`);
      return false;
    }

    try {
      // Load language if not already loaded
      if (!this.loadedLanguages.has(language)) {
        await this.loadLanguageFile(language);
      }

      this.currentLanguage = language;
      this.clearCache(); // Clear cache when language changes
      
      return true;
    } catch (error) {
      console.error(`Failed to change language to ${language}:`, error);
      return false;
    }
  }

  /**
   * Check if language is supported
   * @param {string} language - Language code
   */
  isLanguageSupported(language) {
    return ['fr', 'en'].includes(language);
  }

  /**
   * Get current language
   */
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get available languages
   */
  getAvailableLanguages() {
    return Array.from(this.loadedLanguages);
  }

  /**
   * Clear translation cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      languages: Array.from(this.loadedLanguages),
      currentLanguage: this.currentLanguage
    };
  }
}

// Create singleton instance
const translationService = new TranslationService();

export default translationService;
